import { List, fromJS } from 'immutable';

import { BookingJSON } from './booking-json.model';
import { BookingSerialized } from './booking-serialized.model';
import { Client } from '../client/client.model';
import { Cloneable } from '../cloneable.model';
import { Country } from '../country/country.model';
import { RecurringOptions } from '../recurring-options.model';
import { Serializable } from '../serializable.model';
import { Service } from '../service/service.model';
import { Stylist } from '../stylist/stylist.model';
import { Utilities } from '../../utilities/utilities';

export class Booking
  implements Cloneable<Booking, BookingJSON>, Serializable<BookingSerialized> {
    public readonly id: number;

    public readonly salonID: number;
  
    public readonly startDateTime: Date;
  
    public readonly endDateTime: Date;
  
    public readonly client: Client;
  
    public readonly status: number;
  
    public readonly services: List<Service>;
  
    public readonly recurring: RecurringOptions;
  
    public readonly reminderSent: number;
  
    public readonly sendEmail: number;
  
    public readonly sendSMS: number;
  
    public readonly creationDate: Date;
  
    public readonly type: string;
  
    public readonly photos: List<string>;
  
    public readonly beforePhotos: List<string>;
  
    public readonly afterPhotos: List<string>;
  
    public readonly createdBy: Stylist;

    public readonly createdByPinned: boolean;
  
    public readonly clientIP: string;

    public readonly lastUpdated: string;

    public readonly clientFirstVisit: boolean;

  constructor(options: BookingJSON) {
    this.id = options.id;
    this.salonID = options.salonID;
    this.startDateTime = options.startDateTime;
    this.endDateTime = options.endDateTime;
    this.client = options.client;
    this.status = options.status;
    this.recurring = options.recurring;
    this.reminderSent = options.reminderSent;
    this.sendEmail = options.sendEmail || 0;
    this.sendSMS = options.sendSMS || 0;
    this.creationDate = options.creationDate;
    this.services = options.services || List.of<Service>();
    this.type = options.type;
    this.photos = options.photos || List([]);
    this.beforePhotos = options.beforePhotos || List([]);
    this.afterPhotos = options.afterPhotos || List([]);

    let dateTime = this.startDateTime;
    const length = this.services.count();
    for (let i = 0; i < length; i++) {
      this.services = this.services.update(i, (service: Service) => {
        const updatedService = service.setStartDateTime(dateTime);
        dateTime = updatedService.getEndDateTime();
        return updatedService;
      });
    }
    this.clientIP = options.clientIP;
    this.createdBy = options.createdBy;
    this.createdByPinned = options.createdByPinned;
    this.lastUpdated = options.lastUpdated;
    this.clientFirstVisit = options.clientFirstVisit;
  }



  public static parseBooking(bookingData): Booking {
    const startDateTime: Date = Utilities.parseDate(
      bookingData['bookingStartDateTime']
    );
    const endDateTime: Date = Utilities.parseDate(
      bookingData['bookingEndDateTime']
    );
    const status: number = parseInt(bookingData['status'], 10);
    const clientData = bookingData['client'];

    let country: Country;
    if (clientData.countryID) {
      country = new Country(clientData.countryID, clientData.countryName);
    }

    const client = clientData ? Client.parseClientData(clientData) : undefined;

    let services: List<Service> = List.of<Service>();
    for (const serviceData of bookingData['services']) {
      let service: Service = Service.parseService(serviceData);
      service = service.setClient(client);
      services = services.push(service);
    }

    let recurring: RecurringOptions;
    if (bookingData['recurring'] !== undefined) {
      const recurringID: number = bookingData['recurring'].id;
      const frequencyType: string = bookingData['recurring'].frequencyType;
      const frequencyCount: number = bookingData['recurring'].frequencyCount;

      if (recurringID !== 0) {
        recurring = {
          id: recurringID,
          frequencyType: frequencyType,
          frequencyCount: frequencyCount,

          // default
          change: 'future'
        };
      }
    }

    const bookingOptions: BookingJSON = {
      id: bookingData['id'],
      salonID: bookingData['salonID'],
      startDateTime: startDateTime,
      endDateTime: endDateTime,
      client: client,
      status: status,
      services: services,
      recurring: recurring,
      reminderSent: bookingData['reminderSent'],
      creationDate: Utilities.parseDate(bookingData['creationDate']),
      sendEmail: 0,
      sendSMS: 0,
      type: bookingData['type'],
      photos: bookingData['photos'] ? Utilities.convertToList(fromJS(bookingData['photos'])) : List([]),
      beforePhotos: bookingData['beforePhotos']
        ?  Utilities.convertToList(fromJS(bookingData['beforePhotos']))
        : List([]),
      afterPhotos: bookingData['afterPhotos']
        ?  Utilities.convertToList(fromJS(bookingData['afterPhotos']))
        : List([]),
      clientIP: bookingData['clientIP'],
      lastUpdated: bookingData['lastUpdated'],
      clientFirstVisit: bookingData['clientFirstVisit'],
      createdBy: bookingData['createdBy']
        ? Stylist.parseStylistData(bookingData['createdBy'])
        : undefined
    };

    return new Booking(bookingOptions);
  }

  public serialize(): BookingSerialized {
    const payload: BookingSerialized = {
      id: this.id,
      bookingStartDateTime: Utilities.formatDate(this.startDateTime),
      status: this.status,
      clientID: this.client ? this.client.getID() : undefined,
      services: [],
      recurring: undefined,
      sendEmail: undefined,
      sendSMS: undefined,
      photos: this.photos ? this.photos.toArray() : [],
      beforePhotos: this.beforePhotos ? this.beforePhotos.toArray() : [],
      afterPhotos: this.afterPhotos ? this.afterPhotos.toArray() : []
    };

    this.services.forEach((service: Service) => {
      payload.services.push(service.serialize());
    });

    if (this.recurring) {
      payload.recurring = {
        id: this.recurring.id,
        frequencyType: this.recurring.frequencyType,
        frequencyCount: this.recurring.frequencyCount,
        change: this.recurring.change
      };
    }

    payload.sendEmail = this.sendEmail;
    payload.sendSMS = this.sendSMS

    // Only send it when we actually have it. A placeholder would read as a
    // stale timestamp and get every save rejected.
    if (this.lastUpdated) {
      payload.lastUpdated = this.lastUpdated;
    }

    return payload;
  }

  public toJSON(): BookingJSON {
    return {
      id: this.getId(),
      salonID: this.getSalonID(),
      startDateTime: this.getStartDateTime()
        ? Utilities.cloneDate(this.getStartDateTime())
        : undefined,
      endDateTime: this.getEndDateTime()
        ? Utilities.cloneDate(this.getEndDateTime())
        : undefined,
      client: this.getClient() ? this.getClient().clone() : undefined,
      status: this.getStatus(),
      services: this.services,
      recurring: this.getRecurringSettings()
        ? { ...this.getRecurringSettings() }
        : undefined,
      reminderSent: this.getReminderSent(),
      sendEmail: this.getSendEmail(),
      sendSMS: this.getSendSMS(),
      creationDate: this.getCreationDate(),
      type: this.type,
      photos: this.photos,
      beforePhotos: this.beforePhotos,
      afterPhotos: this.afterPhotos,
      clientIP: this.clientIP,
      createdBy: this.createdBy,
      createdByPinned: this.createdByPinned,
      // Must survive the clone: every setter round-trips through here, so
      // omitting it would drop the precondition on setStartDateTime — the
      // one path the stale-write guard exists to protect.
      lastUpdated: this.lastUpdated,
      // Same reason: the calendar re-clones a booking on drag and status
      // change, and dropping this would silently un-star a new client
      // mid-session with nothing to point at.
      clientFirstVisit: this.clientFirstVisit
    };
  }

  public clone(): Booking {
    return new Booking(this.toJSON());
  }

  public getId(): number {
    return this.id;
  }

  public setId(id: number): Booking {
    const data: BookingJSON = this.toJSON();
    data.id = id;

    // Clearing the id is how both rebooking flows turn a loaded appointment
    // into a new, unsaved one (bookings.page.ts, booking-form-summary.page.ts):
    // clone, drop the id, move the date. clientFirstVisit describes the booking
    // it was computed for, so carrying it over tells the stylist their client
    // is new while they are booking that client's SECOND visit. The server
    // recomputes it on save; until then the honest answer is "unknown".
    if (id === undefined || id === null) {
      data.clientFirstVisit = undefined;
    }

    return new Booking(data);
  }

  public getSalonID(): number {
    return this.salonID;
  }

  public getStartDateTime(): Date {
    return this.startDateTime;
  }

  public getStartDateTimeAsString(): string {
    return Utilities.formatDate(this.startDateTime, 'HH:mm');
  }

  public setStartDateTime(date: Date): Booking {
    const data: BookingJSON = this.toJSON();
    data.startDateTime = date;
    return new Booking(data);
  }

  public getEndDateTime(): Date {
    if (this.services.count() === 0) {
      return this.startDateTime;
    }
    return this.services.last().getEndDateTime();
  }

  public getClient(): Client {
    return this.client;
  }

  public setClient(client: Client): Booking {
    const data: BookingJSON = this.toJSON();
    data.client = client;
    return new Booking(data);
  }

  public getStatus(): number {
    return this.status;
  }

  public setStatus(status: number): Booking {
    const data: BookingJSON = this.toJSON();
    data.status = status;
    return new Booking(data);
  }

  public setType(type: string): Booking {
    const data: BookingJSON = this.toJSON();
    data.type = type;
    return new Booking(data);
  }

  public getReminderSent(): number {
    return this.reminderSent;
  }

  public getServices(): List<Service> {
    return this.services;
  }

  public findServiceById(serviceId: number): Service {
    let s: Service;

    this.services.some((service: Service) => {
      if (service.getId() === serviceId) {
        s = service;
        return true;
      }

      return false;
    });

    return s;
  }

  private generateUniqueServiceID(): number {
    let token = 1;

    while (true) {
      let unique = true;

      // loop through all lineitems to see if this token has been already used

      this.services.some((service: Service) => {
        if (service.getId() === token) {
          unique = false;
          return true;
        }

        return false;
      });

      if (unique) {
        return token;
      } else {
        // generate another token
        token++;
      }
    }
  }

  public addService(newService: Service): Booking {
    // create a temporary ID for this lineitem
    let service = newService.setId(this.generateUniqueServiceID());

    // important, forcce new lineitem to be Phsantom
    service = service.setPhantom(true);

    const data: BookingJSON = this.toJSON();
    data.services = data.services.push(service);

    // Release a pinned label. The pin exists only to hold the answer while the
    // booking has no services; once one is added, "created by" follows the new
    // first service again. Leaving it would freeze the old staff member's name
    // onto a booking they no longer have any service on — delete the only
    // service, add somebody else's, and the footer still named the first.
    if (data.createdByPinned) {
      data.createdBy = undefined;
      data.createdByPinned = undefined;
    }

    return new Booking(data);
  }

  /**
   * The stylist of the group's FIRST booking.
   *
   * Named for exactly what it returns. A booking GROUP has no stylist of its
   * own and can span several (see CONCEPTS.md), so `getStylist()` would be a
   * lie — and conflating the two is what produced SAL-261 in the first place.
   *
   * Public because booking-detail and booking-form-summary both need the same
   * inference; each layers its own remembered-stylist fallback on top.
   */
  public getFirstServiceStylist(): Stylist | undefined {
    return this.services.get(0)?.serviceDefinition?.stylist;
  }

  /**
   * Snapshot of this booking's state with the "created by" label pinned down,
   * for use by a removal that is about to leave the booking with no services.
   *
   * `getCreatedByLabel()` falls back to the first service's stylist whenever
   * the API did not send a `createdBy`, so emptying the services made the
   * label collapse to the bare word "Stylist" — the booking forgot who booked
   * it. Resolving the fallback into the field first makes the answer outlive
   * the service it was read from. `createdBy` is display-only (carried by
   * `toJSON()`, deliberately absent from `serialize()`), so pinning it cannot
   * reach the API.
   *
   * Deliberately confined to the last service. Pinning on every removal would
   * also change the label for `removeServices()`, whose callers immediately
   * add a replacement service — there the existing behaviour of following the
   * NEW first service is the right one, and this must not disturb it.
   */
  private toJSONPinningCreatedBy(): BookingJSON {
    const data: BookingJSON = this.toJSON();

    if (!data.createdBy && !this.isCreatedByClient() && this.services.count() === 1) {
      data.createdBy = this.getFirstServiceStylist();
      // Flagged, not just set: this is a stand-in for a value the API never
      // sent, and addService() releases it so a replacement service takes the
      // label back. Without the flag it is indistinguishable from a genuine
      // API `createdBy`, which must never be released.
      data.createdByPinned = true;
    }

    return data;
  }

  public removeService(serviceID: number): Booking {
    let index = 0;
    let serviceToBeDeleted;

    this.services.some((service) => {
      if (service.getId() === serviceID) {
        serviceToBeDeleted = service;
        return true;
      }

      index++;
      return false;
    });

    if (serviceToBeDeleted !== undefined) {
      const data: BookingJSON = this.toJSONPinningCreatedBy();
      data.services = data.services.remove(index);
      return new Booking(data);
    } else {
      // service to be deleted not found
      throw new Error('invalid ServiceID');
    }
  }


  public removeServiceByLineItemID(lineItemID: number): Booking {
    let index = 0;
    let serviceToBeDeleted;

    this.services.some((service) => {
      if (service.lineItemID === lineItemID) {
        serviceToBeDeleted = service;
        return true;
      }

      index++;
      return false;
    });

    if (serviceToBeDeleted !== undefined) {
      const data: BookingJSON = this.toJSONPinningCreatedBy();
      data.services = data.services.remove(index);
      return new Booking(data);
    } else {
      // service to be deleted not found
      throw new Error('invalid ServiceID');
    }
  }

  public setServices(services: List<Service>) {
    const data: BookingJSON = this.toJSON();
    data.services = services;
    return new Booking(data);
  }

  public removeServices() {
    const data: BookingJSON = this.toJSON();
    data.services = List.of<Service>();
    return new Booking(data);
  }

  public getRecurringSettings(): RecurringOptions {
    return this.recurring;
  }

  public isRecurring(): boolean {
    return this.recurring !== undefined && this.recurring.change !== 'turnoff';
  }

  public isAlreadyLinkedToARecurringAppointment() {
    return this.recurring !== undefined && this.recurring.id !== undefined;
  }

  public setRecurring(options: RecurringOptions) {
    if (this.recurring.id !== undefined && this.recurring.id !== null) {
      throw new Error(
        `Can't set recurring options. Please use setFrequencyCount() or setFrequencyType()`
      );
    }

    const data: BookingJSON = this.toJSON();
    data.recurring = options;
    return new Booking(data);
  }

  public setRecurringFrequencyCount(
    count: number,
    change: string = 'single'
  ): Booking {
    let newRecurringValue: RecurringOptions;

    if (this.recurring === undefined) {
      newRecurringValue = {
        id: undefined,
        frequencyCount: 1,
        frequencyType: 'days'
      };
    } else {
      newRecurringValue = {
        id: this.recurring.id,
        frequencyCount: this.recurring.frequencyCount,
        frequencyType: this.recurring.frequencyType
      };
    }

    if (this.isAlreadyLinkedToARecurringAppointment()) {
      newRecurringValue.change = change;
    }

    newRecurringValue.frequencyCount = count;

    const data: BookingJSON = this.toJSON();
    data.recurring = newRecurringValue;
    return new Booking(data);
  }

  public setRecurringFrequencyType(
    type: string,
    change: string = 'single'
  ): Booking {
    let newRecurringValue: RecurringOptions;

    if (this.recurring === undefined) {
      newRecurringValue = {
        id: undefined,
        frequencyCount: 1,
        frequencyType: 'days'
      };
    } else {
      newRecurringValue = {
        id: this.recurring.id,
        frequencyCount: this.recurring.frequencyCount,
        frequencyType: this.recurring.frequencyType
      };
    }

    if (this.isAlreadyLinkedToARecurringAppointment()) {
      newRecurringValue.change = change;
    }

    newRecurringValue.frequencyType = type;

    const data: BookingJSON = this.toJSON();
    data.recurring = newRecurringValue;
    return new Booking(data);
  }

  public setRecurringChangeOptionType(change: string = 'single'): Booking {
    let newRecurringValue: RecurringOptions;

    if (this.recurring === undefined) {
      newRecurringValue = {
        id: undefined,
        frequencyCount: 1,
        frequencyType: 'days'
      };
    } else {
      newRecurringValue = {
        id: this.recurring.id,
        frequencyCount: this.recurring.frequencyCount,
        frequencyType: this.recurring.frequencyType
      };
    }

    newRecurringValue.change = change;

    const data: BookingJSON = this.toJSON();
    data.recurring = newRecurringValue;
    return new Booking(data);
  }

  public turnOffRecurring(): Booking {
    let newRecurringValue: RecurringOptions;

    if (this.isAlreadyLinkedToARecurringAppointment()) {
      newRecurringValue = this.recurring;
      newRecurringValue.change = 'turnoff';
    } else {
      newRecurringValue = undefined;
    }

    const data: BookingJSON = this.toJSON();
    data.recurring = newRecurringValue;

    return new Booking(data);
  }

  public setSendEmail(type: number): Booking {
    const data: BookingJSON = this.toJSON();
    data.sendEmail = type;
    return new Booking(data);
  }

  public setSendSMS(type: number): Booking {
    const data: BookingJSON = this.toJSON();
    data.sendSMS = type;
    return new Booking(data);
  }

  public setBeforePhotos(photos: List<string>): Booking {
    const data: BookingJSON = this.toJSON();
    data.beforePhotos = photos;
    return new Booking(data);
  }

  public setAfterPhotos(photos: List<string>): Booking {
    const data: BookingJSON = this.toJSON();
    data.afterPhotos = photos;
    return new Booking(data);
  }

  public getSendEmail(): number {
    return this.sendEmail;
  }

  public getSendSMS(): number {
    return this.sendSMS;
  }

  public getCreationDate(): Date {
    return this.creationDate;
  }

  public isCreatedByClient(): boolean {
    return this.clientIP && this.clientIP !== '';
  }

  /**
   * True only when the API positively said so. A booking read from an endpoint
   * that does not compute the flag leaves it undefined, and "we didn't ask" has
   * to render the same as "not a first visit" — never as a star.
   */
  public isClientFirstVisit(): boolean {
    return this.clientFirstVisit === true;
  }

  public getCreatedByLabel(): string {
    if (this.isCreatedByClient()) {
      if (this.client) {
        return `${this.client.firstName} ${this.client.lastName}`;
      } else {
        return `Client`;
      }
    } else {
      if (this.createdBy) {
        return `${this.createdBy.firstName} ${this.createdBy.lastName}`;
      } else {
        if (this.services.count() === 0) {
          return 'Stylist';
        } else {
          const s: Stylist = this.getFirstServiceStylist();
          return `${s.firstName} ${s.lastName}`;
        }
      }
    }
  }

  public willReminderUsersHoursBefore(): number {
    let remindUserHoursBefore = 0;

    if (this.type === 'service') {
      this.services.forEach((service) => {
        if (
          service.getStylist().getSendAppointmentReminderBefore() >
          remindUserHoursBefore
        ) {
          remindUserHoursBefore = service
            .getStylist()
            .getSendAppointmentReminderBefore();
        }
      });
    }

    return remindUserHoursBefore;
  }
}
